import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useAuthSubAgentConfig } from '@/components/organisms/Dialogs/Action/SubAgent/useAuthSubAgentConfig';
import AuthPageFailurePanel from '@/components/pages/Authentication/panels/AuthPageFailurePanel';
import AuthPageStepPanel from '@/components/pages/Authentication/panels/AuthPageStepPanel';
import AuthPageSuccessPanel from '@/components/pages/Authentication/panels/AuthPageSuccessPanel';
import { AuthStepKey } from '@/components/pages/Authentication/types';
import { selectSelectedAction } from '@/redux/dialogs/selectors';
import { selectAuthPanel } from '@/redux/nodes/actions';
import { selectSelectedAuthPanel } from '@/redux/nodes/selectors';
import { SubAgentAction } from '@/shared/types/action';
import { AuthPanel } from '@/shared/types/node';

const parsePanelStep = (panel: AuthPanel): AuthStepKey =>
  panel.endsWith('-verification') ? 'verification' : 'identification';

/**
 * Right-hand toolkit panel shown when a `sub_agent` (auth) action is selected.
 * The active subpanel (step / success / failure) is driven by
 * `state.nodes.selectedAuthPanel`, which `AuthActionCard` updates as the user
 * clicks the inline pieces of the steps canvas.
 */
const ToolkitSubAgentAuth = () => {
  const dispatch = useDispatch();
  const selected = useSelector(selectSelectedAction);
  const selectedPanel = useSelector(selectSelectedAuthPanel) ?? 'step-identification';
  const action = selected?.type === 'sub_agent' ? (selected as SubAgentAction) : null;

  const handleClose = useCallback(() => {
    dispatch(selectAuthPanel({ panel: 'step-identification' }));
  }, [dispatch]);

  // Hook can be called unconditionally; pass a no-op shape when the selected
  // action is not a sub_agent. In practice this branch never renders because
  // renderActionToolkit gates by `selectedType === 'sub_agent'`.
  const { authConfig, onUpdate } = useAuthSubAgentConfig(
    action?.action_id ?? '',
    action ?? ({} as SubAgentAction)
  );

  if (!action) {
    return null;
  }

  const step = parsePanelStep(selectedPanel);
  const showNodeSelector =
    (authConfig.verification_step?.enabled ? 'verification' : 'identification') === step;

  if (selectedPanel.startsWith('success-')) {
    return (
      <AuthPageSuccessPanel
        key={selectedPanel}
        step={step}
        showNodeSelector={showNodeSelector}
        authConfig={authConfig}
        onUpdate={onUpdate}
        onClose={handleClose}
        showErrors
      />
    );
  }

  if (selectedPanel.startsWith('failure-')) {
    return (
      <AuthPageFailurePanel
        key={selectedPanel}
        step={step}
        showNodeSelector={showNodeSelector}
        authConfig={authConfig}
        onUpdate={onUpdate}
        onClose={handleClose}
        showErrors
      />
    );
  }

  return (
    <AuthPageStepPanel
      key={selectedPanel}
      step={step}
      authConfig={authConfig}
      onUpdate={onUpdate}
      onClose={handleClose}
      showErrors
    />
  );
};

export default ToolkitSubAgentAuth;
